import { InputHTMLAttributes, ReactElement } from 'react';

import { Input as OrionInput } from '@iwoca/orion';
import cn from 'classnames';
import { useField } from 'formik';
import { useFormContext } from 'react-hook-form';

import { extractError } from '../../utils/ReactHookForm';
import { InputError } from '../InputError/InputError';

type TCompatInputType =
  | Parameters<typeof OrionInput>[0]['type']
  | 'hidden'
  | 'date-input';
export const Input = ({
  label,
  labelDescriptionText,
  name,
  className,
  Icon,
  showError = true,
  formatter,
  type,
  ...inputProps
}: {
  name: string;
  label?: string | ReactElement;
  labelDescriptionText?: string;
  className?: string;
  showError?: boolean;
  Icon?: React.ReactNode;
  formatter?: (value: string) => string | undefined;
  type?: TCompatInputType;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const [field, { error, touched }, { setValue }] = useField(name);

  const hasIcon = Boolean(Icon);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    inputProps.onChange?.(e);
    field.onChange(e);
  };

  return (
    <div className={cn(className)}>
      <div className="relative">
        <span className="absolute left-l top-[50%] translate-y-[-50%]">
          {Icon}
        </span>
        <OrionInput
          {...inputProps}
          {...field}
          // Expecting error due to orion not supporting "hidden"
          // @ts-expect-error
          type={type}
          label={label}
          description={labelDescriptionText}
          onChange={onChange}
          id={name}
          name={name}
          className={cn(
            'box-border !border !border-[var(--color-structure-80)] [&>div]:flex',
            {
              'pl-[30px]': hasIcon,
            },
          )}
          onKeyUp={async () => {
            if (!formatter) return;
            const formattedInput = formatter(field.value);

            if (formattedInput === undefined) return;

            await setValue(formattedInput);
          }}
        />
      </div>
      <InputError isVisible={touched && showError} error={error} />
    </div>
  );
};

export const HookFormInput = ({
  id,
  label,
  labelDescriptionText,
  name,
  className,
  Icon,
  showError = true,
  formatter,
  onChange,
  type,
  ...inputProps
}: {
  id?: string;
  name: string;
  label?: string | ReactElement;
  labelDescriptionText?: string;
  className?: string;
  showError?: boolean;
  Icon?: React.ReactNode;
  iconLeft?: Parameters<typeof OrionInput>[number]['iconLeft'];
  formatter?: (value: string) => string | undefined;
  type?: TCompatInputType;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const { register, formState, watch, setValue } = useFormContext();
  const error = extractError(formState.errors[name]);

  const field = watch(name);

  const hasIcon = Boolean(Icon);

  const identifier = id || name;

  return (
    <div className={cn(className)}>
      <div className="relative">
        {hasIcon ? (
          <span
            className="absolute left-l top-[50%] translate-y-[-50%]"
            aria-hidden={true}
          >
            {Icon}
          </span>
        ) : null}
        <OrionInput
          {...inputProps}
          // Expecting error due to orion not supporting "hidden"
          // @ts-expect-error
          type={type}
          label={label}
          description={labelDescriptionText}
          {...register(name, {
            onChange: (e) => {
              onChange?.(e);
            },
          })}
          id={identifier}
          className={cn(
            'box-border !border !border-[var(--color-structure-80)] [&>div]:flex',
            {
              'pl-[30px]': hasIcon,
            },
          )}
          onKeyUp={() => {
            if (!formatter) return;
            const formattedInput = formatter(field);

            if (formattedInput === undefined) return;

            setValue(name, formattedInput);
          }}
        />
      </div>
      {showError && <InputError isVisible={true} error={error} />}
    </div>
  );
};
